import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, keys } from '../../api.ts';
import { competitionSchema, type CompetitionInput } from '../../../shared/schemas.ts';
import {
  applyServerErrors,
  BandPicker,
  ChipSelect,
  CountryPicker,
  ErrorBox,
  Field,
  Loading,
  ModePicker,
  PageHeader,
  Spinner,
  useDocumentTitle,
  useToast,
} from '../../components/ui.tsx';
import { useCompetition } from './CompetitionPage.tsx';

const num = (v: string) => (v === '' ? null : Number(v));

export function CompetitionEditPage() {
  const params = useParams();
  const id = params.id ? Number(params.id) : null;
  const existing = useCompetition(id ?? 0);
  useDocumentTitle(id ? 'Muokkaa kilpailua' : 'Uusi kilpailu');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { register, control, handleSubmit, formState, reset, setError } = useForm<CompetitionInput, unknown, z.output<typeof competitionSchema>>({
    resolver: zodResolver(competitionSchema),
    defaultValues: {
      heading: '',
      description: '',
      startDate: '',
      endDate: '',
      rules: { licenceMinYears: null, licenceMaxYears: null, bands: [], modes: [], countries: [], genres: [], classes: [] },
    },
  });
  useEffect(() => {
    const c = existing.data?.competition;
    if (id && c) reset({ heading: c.heading, description: c.description, startDate: c.startDate, endDate: c.endDate, rules: c.rules });
  }, [existing.data, id, reset]);

  const save = useMutation({
    mutationFn: (d: z.output<typeof competitionSchema>) =>
      id ? api.put(`/competitions/${id}`, d).then(() => ({ id })) : api.post<{ id: number }>('/competitions', d),
    onSuccess: (r) => {
      toast.ok('Kilpailu tallennettu');
      void qc.invalidateQueries({ queryKey: keys.competitions });
      void qc.invalidateQueries({ queryKey: keys.competition(r.id) });
      navigate(`/competitions/${r.id}`);
    },
    onError: (err) => !applyServerErrors(err, setError) && toast.error(err),
  });

  if (id && existing.isLoading) return <Loading />;
  if (id && existing.error) return <ErrorBox error={existing.error} />;
  const e = formState.errors;

  return (
    <div className="page page-narrow">
      <PageHeader title={id ? 'Muokkaa kilpailua' : 'Uusi kilpailu'} />
      <form className="card form" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
        <Field label="Otsikko" error={e.heading} htmlFor="heading">
          <input id="heading" className="input" {...register('heading')} />
        </Field>
        <Field label="Kuvaus ja säännöt" error={e.description} htmlFor="description">
          <textarea id="description" rows={6} {...register('description')} />
        </Field>
        <div className="form-grid">
          <Field label="Alkaa" error={e.startDate} htmlFor="startDate">
            <input id="startDate" type="date" className="input" {...register('startDate')} />
          </Field>
          <Field label="Päättyy" error={e.endDate} htmlFor="endDate">
            <input id="endDate" type="date" className="input" {...register('endDate')} />
          </Field>
        </div>
        <fieldset>
          <legend>Osallistujat</legend>
          <div className="form-grid">
            <Field label="Lisenssi vähintään (v)" htmlFor="lmin">
              <input id="lmin" className="input mono" inputMode="numeric" {...register('rules.licenceMinYears', { setValueAs: num })} />
            </Field>
            <Field label="Lisenssi alle (v)" htmlFor="lmax">
              <input id="lmax" className="input mono" inputMode="numeric" {...register('rules.licenceMaxYears', { setValueAs: num })} />
            </Field>
          </div>
          <div className="grid grid-2" style={{ marginTop: 12 }}>
            <div>
              <div className="label">Luokka (tyhjä = kaikki)</div>
              <Controller
                control={control}
                name="rules.classes"
                render={({ field }) => (
                  <ChipSelect
                    options={[
                      { value: 'Yleisluokka', label: 'Yleisluokka' },
                      { value: 'Perusluokka', label: 'Perusluokka' },
                    ]}
                    value={(field.value ?? []) as Array<'Yleisluokka' | 'Perusluokka'>}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div>
              <div className="label">Sukupuoli (tyhjä = kaikki)</div>
              <Controller
                control={control}
                name="rules.genres"
                render={({ field }) => (
                  <ChipSelect
                    options={[
                      { value: 'OM', label: 'OM' },
                      { value: 'YL', label: 'YL' },
                    ]}
                    value={(field.value ?? []) as Array<'OM' | 'YL'>}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend>Hyväksyttävät QSO:t (tyhjä = kaikki)</legend>
          <div className="stack">
            <div>
              <div className="label">Bandit</div>
              <Controller control={control} name="rules.bands" render={({ field }) => <BandPicker value={field.value ?? []} onChange={field.onChange} />} />
            </div>
            <div>
              <div className="label">Moodit</div>
              <Controller control={control} name="rules.modes" render={({ field }) => <ModePicker value={field.value ?? []} onChange={field.onChange} />} />
            </div>
            <div>
              <div className="label">Maat</div>
              <Controller control={control} name="rules.countries" render={({ field }) => <CountryPicker value={field.value ?? []} onChange={field.onChange} />} />
            </div>
          </div>
        </fieldset>
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : 'Tallenna kilpailu'}
          </button>
        </div>
      </form>
    </div>
  );
}
